import { useEffect, useState } from "react";
import { api } from "../../api";
import { Check, X as XIcon, Bot, Clock, ChevronRight, Pencil, FilePlus, Archive } from "lucide-react";
import { ProposalRecordEditor, clearSchemaCache } from "./ProposalRecordEditor";

const TYPE_META = {
  edit: { label: "Edit", icon: Pencil, color: "var(--warn)" },
  create: { label: "New record", icon: FilePlus, color: "var(--mint)" },
  archive: { label: "Archive", icon: Archive, color: "var(--danger)" },
};
const errText = (ex) => { const m = String(ex.message || ex); try { return JSON.parse(m).detail || m; } catch { return m; } };

/**
 * Change Requests — admin review page for proposals coming in through the
 * MCP server. Each row shows the proposed field change, the reason, and
 * Approve/Reject buttons. Approve applies the change via the normal entry
 * update path (bumps version, logs audit). Reject just marks the row.
 */
export default function ChangeRequests({ user }) {
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("pending");

  const load = async () => {
    setErr("");
    clearSchemaCache();
    try {
      const r = await api.changeRequests.list();
      setRows(r.items || []);
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (id, kind, data = null) => {
    const note = window.prompt(kind === "approve" ? "Optional note on this approval (visible in the audit log):" : "Reason for rejection (shown on the proposal):", "");
    if (note === null) return;
    try {
      if (kind === "approve") await api.changeRequests.approve(id, note, data);
      else await api.changeRequests.reject(id, note);
      window.dispatchEvent(new Event("ye:counts-changed"));
      await load();
    } catch (ex) {
      alert(errText(ex));
    }
  };

  const filtered = rows.filter(r => (r.status === "approving" ? "pending" : r.status) === tab);

  if (!isAdmin) {
    return (
      <div className="panel" style={{ padding: 40, textAlign: "center" }} data-testid="change-requests-forbidden">
        <div className="muted" style={{ fontSize: 13 }}>Only admins can review AI change proposals.</div>
      </div>
    );
  }

  return (
    <div data-testid="change-requests-page">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
        <Bot size={18} style={{ color: "var(--mint)" }} />
        <div>
          <h2 style={{ fontSize: 20, margin: 0 }}>Change Requests</h2>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>Proposals from AI assistants connected via MCP. Nothing is applied until you approve.</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {["pending", "approved", "rejected"].map(s => {
          const n = rows.filter(r => (r.status === "approving" ? "pending" : r.status) === s).length;
          return (
            <button
              key={s}
              onClick={() => setTab(s)}
              data-testid={`cr-tab-${s}`}
              className={`btn ${tab === s ? "btn-primary" : ""}`}
              style={{ padding: "6px 12px", fontSize: 12, textTransform: "capitalize" }}
            >
              {s} · {n}
            </button>
          );
        })}
      </div>

      {err && <div className="panel" style={{ color: "var(--danger)", padding: 14, fontSize: 12, marginBottom: 14 }}>{err}</div>}
      {loading ? (
        <div className="panel muted" style={{ padding: 30, textAlign: "center" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="panel muted" style={{ padding: 30, textAlign: "center", fontSize: 13 }} data-testid={`cr-empty-${tab}`}>
          No {tab} change requests.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map(r => (
            <ProposalCard key={r.id} r={r} onApprove={(data) => act(r.id, "approve", data)} onReject={() => act(r.id, "reject")} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProposalCard({ r, onApprove, onReject }) {
  const pending = r.status === "pending";
  const type = r.type || "edit";
  const meta = TYPE_META[type] || TYPE_META.edit;
  const TypeIcon = meta.icon;
  const [draft, setDraft] = useState(r.proposed_record || {});
  const statusColor = r.status === "approved" ? "var(--mint)" : r.status === "rejected" ? "var(--danger)" : "var(--warn)";
  return (
    <div className="panel" data-testid={`cr-card-${r.id}`} style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span className="badge" data-testid={`cr-type-${r.id}`} style={{ fontSize: 10, padding: "3px 8px", background: "var(--panel2)", color: meta.color, textTransform: "uppercase", letterSpacing: "0.08em", display: "inline-flex", alignItems: "center", gap: 4 }}><TypeIcon size={10} /> {meta.label}</span>
          <span className="badge" style={{ fontSize: 10, padding: "3px 8px", background: "var(--panel2)", color: statusColor, textTransform: "uppercase", letterSpacing: "0.08em" }}>{r.status}</span>
          <code style={{ fontSize: 11.5, background: "var(--panel2)", padding: "2px 7px", borderRadius: 4 }}>{r.department || r.collection}</code>
          {type === "edit" && <><ChevronRight size={11} style={{ color: "var(--muted)" }} />
          <code style={{ fontSize: 11.5, background: "var(--panel2)", padding: "2px 7px", borderRadius: 4 }}>{r.field}</code></>}
        </div>
        <div className="muted" style={{ fontSize: 10.5, display: "flex", alignItems: "center", gap: 4 }}>
          <Clock size={11} /> {new Date(r.requested_at).toLocaleString()}
        </div>
      </div>

      <div style={{ display: "grid", gap: 8, marginBottom: 10, background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 8, padding: 12 }}>
        {type === "edit" && <>
          <Row label="Current"><code style={{ color: "var(--danger)", textDecoration: "line-through", wordBreak: "break-word" }}>{fmt(r.current_value)}</code></Row>
          <Row label="Proposed"><code style={{ color: "var(--mint)", wordBreak: "break-word" }}>{fmt(r.proposed_value)}</code></Row>
        </>}
        {type === "create" && (pending
          ? <ProposalRecordEditor dept={r.department} value={draft} onChange={setDraft} testId={`cr-editor-${r.id}`} />
          : <RecordFields data={r.applied_value || r.proposed_record} />)}
        {type === "archive" && <>
          <div className="muted" style={{ fontSize: 11 }}>{pending ? "This record will be hidden and moved to Recently Deleted (restorable)." : r.status === "approved" ? "Archived — restorable from Recently Deleted." : "Not archived."}</div>
          <RecordFields data={r.current_value} />
        </>}
      </div>

      {r.reason && (
        <div className="muted" style={{ fontSize: 12, lineHeight: 1.5, marginBottom: 10, fontStyle: "italic", borderLeft: "2px solid var(--border)", paddingLeft: 10 }}>
          “{r.reason}”
        </div>
      )}

      <div className="muted" style={{ fontSize: 10.5, marginBottom: pending ? 12 : 4 }}>
        {(r.entry_id || r.created_entry_id) && <>On record: <code style={{ background: "var(--panel2)", padding: "1px 6px", borderRadius: 3 }}>{r.entry_id || r.created_entry_id}</code> · </>}requested by <code style={{ background: "var(--panel2)", padding: "1px 6px", borderRadius: 3 }}>{r.requested_by}</code>
      </div>

      {!pending && (
        <div className="muted" style={{ fontSize: 11, lineHeight: 1.5 }}>
          {r.status === "approved" ? "Approved" : "Rejected"} by <b>{r.reviewed_by}</b> on {new Date(r.reviewed_at).toLocaleString()}
          {r.review_note && <> — <span style={{ fontStyle: "italic" }}>“{r.review_note}”</span></>}
        </div>
      )}

      {pending && (
        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button onClick={() => onApprove(type === "create" ? draft : null)} className="btn btn-primary" data-testid={`cr-approve-${r.id}`} style={{ padding: "7px 14px", fontSize: 12 }}>
            <Check size={13} /> {type === "create" ? "Approve & create" : type === "archive" ? "Approve & archive" : "Approve & apply"}
          </button>
          <button onClick={onReject} className="btn" data-testid={`cr-reject-${r.id}`} style={{ padding: "7px 14px", fontSize: 12, color: "var(--danger)" }}>
            <XIcon size={13} /> Reject
          </button>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 12 }}>
      <span className="muted" style={{ width: 110, flexShrink: 0, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 10 }}>{label}</span>
      {children}
    </div>
  );
}

function RecordFields({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return <div className="muted" style={{ fontSize: 12 }}>No fields.</div>;
  return entries.map(([k, v]) => <Row key={k} label={k.replace(/_/g, " ")}><code style={{ wordBreak: "break-word" }}>{fmt(v)}</code></Row>);
}

function fmt(v) {
  if (Array.isArray(v) && v.length && v.every(x => x && typeof x === "object" && "percent" in x)) return v.map(x => `${x.name} (${x.role}) ${x.percent}%`).join(" · ");
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v); } catch { return String(v); }
}
